import { type ComponentProps, type PropsWithChildren } from "react";
import { ToggleButton } from "react-aria-components";
import XCircleSolid from "src/design-system/icons/XCircleSolid.svg?react";
import { Icon } from "../Icon/Icon";
import { tw } from "../../tw";
import styles from "./Chip.module.css";

type Props = PropsWithChildren<
  ComponentProps<typeof ToggleButton> & {
    isSelected: boolean;
    icon?: React.ReactNode;
    disabledIcon?: React.ReactNode;
    leftIcon?: React.ReactNode;
    handleClear?: () => void;
    filterSelection?: string;
    isBlanketVisible?: boolean;
  }
>;

export function ToggleChip({
  children,
  isSelected,
  icon,
  leftIcon,
  isDisabled,
  disabledIcon,
  onPress,
  handleClear,
  filterSelection,
  isBlanketVisible,
  ...delegated
}: Props) {
  const chipStyles = styles["chip"];
  const chipText = styles["chip-text"];
  const chipIcon = styles["chip-icon"];
  const chipClear = styles["chip-clear"];
  const chipDisabledIcon = styles["chip-disabled-icon"];

  const showClearButton = !!(handleClear && filterSelection && !isDisabled);
  const showDisabledIcon = !!(disabledIcon && isDisabled && !isSelected);

  return (
    <div style={{ position: "relative" }}>
      <ToggleButton
        className={chipStyles}
        onPress={onPress}
        isDisabled={isDisabled}
        isSelected={isSelected || !!filterSelection}
        data-has-clear={showClearButton || undefined}
        data-blanket-visible={isBlanketVisible || undefined}
        {...delegated}
      >
        {leftIcon && <Icon size="small">{leftIcon}</Icon>}
        <span className={tw(chipText, "text-body-md")}>{children}</span>
        {icon && !showClearButton && !showDisabledIcon && (
          <Icon className={chipIcon}>{icon}</Icon>
        )}
        {showDisabledIcon && (
          <Icon className={chipDisabledIcon}>{disabledIcon}</Icon>
        )}
      </ToggleButton>

      {showClearButton && (
        <Icon
          size="small"
          className={chipClear}
          onClick={(e) => {
            e.stopPropagation();
            handleClear();
          }}
        >
          <XCircleSolid className="h-4.5 w-4.5 text-icon-secondary" />
        </Icon>
      )}
    </div>
  );
}
